import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDateTime, timeAgo } from "@/lib/format";

export default function Members() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [busy, setBusy] = useState(false);
  const members = useQuery({ queryKey: ["members"], queryFn: () => api.get("/members").then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["members"] });

  const add = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/members", { email, is_admin: isAdmin }); toast.success(`${email} can now sign in`); setEmail(""); setIsAdmin(false); refresh(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const setRole = async (m, v) => {
    try { await api.patch(`/members/${m.id}`, { is_admin: v }); refresh(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.email}? They will be signed out everywhere.`)) return;
    try { await api.delete(`/members/${m.id}`); toast.success(`${m.email} removed`); refresh(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="rise flex flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-100">Members</h1>
        <p className="mt-1 text-sm text-zinc-400">Only these emails can sign in to the hub and the extension. Removing someone signs them out within a minute.</p>
      </div>

      <form onSubmit={add} className="surface flex flex-wrap items-center gap-3 p-4" data-testid="add-member-form">
        <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@yourcompany.com" className="max-w-sm flex-1 bg-[#0B0D11]" data-testid="add-member-email-input" />
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <Checkbox checked={isAdmin} onCheckedChange={(v) => setIsAdmin(!!v)} data-testid="add-member-admin-checkbox" /> Admin
        </label>
        <Button type="submit" disabled={busy || !email} className="gap-2" data-testid="add-member-button"><UserPlus className="h-4 w-4" /> Add member</Button>
      </form>

      <div className="surface overflow-x-auto">
        <table className="table-dense w-full" aria-label="Members" data-testid="members-table">
          <thead className="border-b border-[#242A36]"><tr><th>Email</th><th>Admin</th><th>Added</th><th>Last sign-in</th><th></th></tr></thead>
          <tbody>
            {(members.data || []).map((m) => (
              <tr key={m.id} data-testid={`member-row-${m.id}`}>
                <td>
                  <span className="text-zinc-200">{m.email}</span>
                  {m.email === user?.email && <span className="ml-2 text-xs text-zinc-500">(you)</span>}
                  {m.from_env && <span className="ml-2 rounded border border-zinc-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-zinc-500" title="Set via ADMIN_EMAILS on the server">config</span>}
                </td>
                <td><Switch checked={m.is_admin} onCheckedChange={(v) => setRole(m, v)} disabled={m.from_env} data-testid={`member-admin-switch-${m.id}`} /></td>
                <td className="text-xs text-zinc-500">{fmtDateTime(m.created_at)}{m.added_by && m.added_by !== "env" && m.added_by !== "bootstrap" ? ` · by ${m.added_by}` : ""}</td>
                <td className="text-xs text-zinc-500">{m.last_sign_in_at ? timeAgo(m.last_sign_in_at) : "never"}</td>
                <td className="text-right">
                  {!m.from_env && (
                    <Button size="sm" variant="ghost" onClick={() => remove(m)} className="text-rose-400 hover:text-rose-300" data-testid={`member-remove-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
